import { Box, Button, Paper } from "@mui/material";
import { useEffect, useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";

import FernieMicro from "~/components/fernie/FernieMicro";
import FernieName from "~/components/fernie/FernieName";
import { BodyTextSmall } from "~/components/Typographies";
import { useAppSelector } from "~/hooks/reduxHooks";
import useStartTour from "~/hooks/useStartTour";
import useWalkthroughState from "~/hooks/useWalkthroughState";
import { useGetTenantQuery } from "~/services/endpoints/apiEndpoints";
import { AI_PANEL_WIDTH } from "~/store/layoutSlice";
import { fernieColors } from "~/themes/fernieBrand";
import { findWalkthroughsForRoute } from "~/walkthroughs/registry";
import pickTourOffer from "~/walkthroughs/tourOffer";
import { type WalkthroughDefinition } from "~/walkthroughs/types";

interface TourOfferCardProps {
	helpOn: boolean;
	isWideScreen: boolean;
	pathname: string;
}

const TourOfferCard = ({ helpOn, isWideScreen, pathname }: TourOfferCardProps) => {
	const { t } = useTranslation("common");
	const startTour = useStartTour();
	const { isLoaded, markDismissed, markOffered, offered } = useWalkthroughState();
	const activeTourId = useAppSelector((state) => state.walkthrough.activeTourId);
	const status = useAppSelector((state) => state.walkthrough.status);
	const tours = useAppSelector((state) => findWalkthroughsForRoute(pathname, state), shallowEqual);
	const isFerniePanelDocked = useAppSelector((state) => state.layout.aiPanelOpen && !state.layout.aiPanelPoppedOut);
	const [tour, setTour] = useState<WalkthroughDefinition | null>();
	const messageId = useId();

	useEffect(() => {
		if (!isLoaded || tour !== undefined) return;
		const offer = pickTourOffer({ helpOn, isLoaded, isWideScreen, offered, status, tours });
		if (!offer) return;
		setTour(offer);
		void markOffered(offer.id);
	}, [helpOn, isLoaded, isWideScreen, markOffered, offered, status, tour, tours]);

	useEffect(() => {
		if (status !== "running") return;
		setTour(null);
		if (activeTourId && !offered(activeTourId)) void markOffered(activeTourId);
	}, [activeTourId, markOffered, offered, status]);

	if (!tour) return null;

	const dismiss = () => {
		setTour(null);
		void markDismissed(tour.id, t("walkthrough.offer.dismissed"));
	};

	return (
		<Paper
			aria-labelledby={messageId}
			elevation={8}
			role="dialog"
			sx={{
				border: `1px solid ${fernieColors.springSoft}`,
				borderRadius: "14px",
				bottom: 24,
				position: "fixed",
				px: 2,
				py: 1.75,
				right: isFerniePanelDocked ? AI_PANEL_WIDTH + 24 : 24,
				width: 300,
				zIndex: (theme) => theme.zIndex.appBar
			}}
		>
			<Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 0.75 }}>
				<FernieMicro decorative size={22} />
				<FernieName color={fernieColors.richSoil} fontSize={15} />
			</Box>
			<BodyTextSmall id={messageId} sx={{ color: "text.secondary", fontSize: "0.85rem", lineHeight: 1.5 }}>
				<Trans
					components={{ b: <strong />, fernie: <FernieName color={fernieColors.richSoil} /> }}
					i18nKey="walkthrough.offer.message"
					t={t}
					values={{ title: t(tour.titleKey) }}
				/>
			</BodyTextSmall>
			<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "flex-end", mt: 1.5 }}>
				<Button onClick={dismiss} size="small" sx={{ color: fernieColors.dim, textTransform: "none" }}>
					{t("walkthrough.offer.dismiss")}
				</Button>
				<Button
					onClick={() => startTour(tour.id)}
					size="small"
					sx={{
						"&:hover": { backgroundColor: fernieColors.fern },
						backgroundColor: fernieColors.fern,
						borderRadius: "8px",
						color: "common.white",
						px: 2,
						textTransform: "none"
					}}
					variant="contained"
				>
					{t("walkthrough.offer.show")}
				</Button>
			</Box>
		</Paper>
	);
};

const WalkthroughOfferNote = () => {
	const { pathname } = useLocation();
	const { data } = useGetTenantQuery();
	const helpOn = Boolean(data?.tenant?.onboardingHelpOn);
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);

	if (!helpOn || !isWideScreen) return null;
	return <TourOfferCard helpOn={helpOn} isWideScreen={isWideScreen} key={pathname} pathname={pathname} />;
};

export default WalkthroughOfferNote;
